'use client';

import {
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  useTransition,
} from 'react';
import dynamic from 'next/dynamic';
import { useRouter, useSearchParams } from 'next/navigation';
import posthog from 'posthog-js';
import * as Sentry from '@sentry/nextjs';
import { captureError } from '@/lib/sentry';
import type { TutorBundle } from '@peach/types/tutors/tutor-profile.dto.js';
import { tw } from '@tw';
import { getPlatformFee } from '@peach/types/payments/fees.js';
import { INTRO_ALREADY_BOUGHT } from '@peach/types/bundles/types.js';
import { formatPrice } from '@peach/types/utils/currencies.js';
import { callNoun } from '@/lib/utils/bookable-product';
import { createCheckout, expireCheckout } from '@/app/actions/orders';
import { hasPromoParam } from '@/lib/utils/promo';
import { recordTeammateHop } from '@/app/actions/squad';
import { InlineAuth } from '@/components/auth/InlineAuth';
import type { AuthSource } from '@/lib/analytics/auth';
import { useBundlesStore } from '@/components/products/use-bundles-store';
import { BUNDLE_TITLE_MAX, BundleType, getDefaultTitle } from '@/components/products/bundle-config';
import { InlineText } from './InlineText';
import { OWNER_TRIAL_LINE } from './intro-copy';
import { ProductEditor } from './ProductEditor';
import { EditButton, SaveCancel } from './EditActions';
import { useCutSale, useOpenCard } from './checkout-store';
import { useFreshPass } from '@/lib/stores/fresh-pass.store';
import type { PaymentStatusDto } from '@peach/types/payments/payment-status.dto.js';
import { usePaymentPolling } from './usePaymentPolling';
import { callAction } from '@/lib/call-action';

const EmbeddedCheckoutPanel = dynamic(() => import('@/components/payment/EmbeddedCheckoutPanel'), {
  ssr: false,
});

/**
 * Everything the card actually reads. Narrower than `TutorBundle` on purpose:
 * the owner's grid renders cards straight off the editing draft, which has no
 * `ownerId` and never needs one.
 */
export type ProductCardBundle = Pick<
  TutorBundle,
  'id' | 'price' | 'previousPrice' | 'title' | 'numberOfClasses' | 'currency' | 'type' | 'duration'
>;

interface ProductCardProps {
  bundle: ProductCardBundle;
  tutorUsername: string;
  loggedIn: boolean;
  /** The owner previewing their own page. Analytics only — an owner opening
      their own product is checking it, not shopping. */
  isOwner?: boolean;
  /** The owner's own products grid: the title is typed into, and the card's
      settings stand open under it. Never set on the flat variants. */
  editable?: boolean;
  flat?: boolean;
  /** What ground the flat variant sits on: 'accent' (a coloured card — quiz,
      teammate) keeps white text and the white chip; 'card' (a white card — the
      calls block) flips to ink text and an ink chip. The non-flat card is its
      own coloured surface, so this only matters with `flat`. */
  surface?: 'accent' | 'card';
  /** Overrides the bundle's own title; `null` renders no heading at all. */
  headline?: string | null;
  autoOpen?: boolean;
  /** Where this card sits, for signup attribution — the same card is the quiz's
      closing offer, and a lead won there is not a lead won off the grid. */
  authSource?: AuthSource;
  /** Set when this card sells a *teammate's* product on someone else's
      storefront: the alias of the referral edge it is sold under. The card
      records the hop with it before checkout, and sends it in the sign-in body
      for a signed-out buyer. Never a URL param, in either case. */
  squadAlias?: string | null;
  /** A call picked before paying (the quiz result): sent with the checkout so
      the webhook books it. The card itself says nothing about it — the picker
      above already shows the time. */
  slot?: PickedSlot | null;
  /** Replaces the sign-in prompt for a signed-out buyer. The calls card and the
      quiz result name the hour they picked, which is what they are signing in
      for; the plain grid has nothing better to say than the product's name. */
  authPrompt?: string;
  /** Replaces the chip's verb ("Get 15 min"). The price stays where it is —
      whatever this says, it is read against the number beside it. */
  actionLabel?: string;
  /** The product is shown, not sold: this sentence stands where the chip would.
      A client who has had this creator's intro reads why they cannot buy it again. */
  note?: string;
  /** The picked time was sold while the card stood there. The message belongs
      beside the rail that fixes it, so whoever owns the calendar takes it. */
  onSlotTaken?: () => void;
  /** The grid's card: once paid it stands as the pass it bought, so the buyer
      books where they paid. Elsewhere the page re-lays itself around the pass. */
  holdsPass?: boolean;
  /** A page that is not live yet: the card reads as a visitor's, and its button opens nothing. */
  preview?: boolean;
}

export type PickedSlot = { start: string; timezone: string };

/** One sentence for the card and for checkout's refusal, so they cannot drift. */
export const INTRO_ALREADY_BOUGHT_COPY =
  "You've already had your intro call here, so it can't be bought again";

export function withFees(amount: number): number {
  return Number((amount + getPlatformFee(amount)).toFixed(2));
}

/**
 * The two numbers a price is: what lands in the owner's wallet, first and
 * whole, then what leaves the client's card. Fees ride on top of the price
 * rather than out of it, and the card beside the owner quotes the second.
 */
export function priceSplitNote(price: number, currency: string): string {
  return `You get the full ${formatPrice({ amount: price, currency })}. Your client pays ${formatPrice({ amount: withFees(price), currency })}, fees included.`;
}

export function ProductCard({
  bundle,
  tutorUsername,
  loggedIn,
  isOwner = false,
  editable = false,
  flat = false,
  surface = 'accent',
  headline,
  autoOpen = false,
  authSource = 'product_card',
  squadAlias,
  slot = null,
  authPrompt,
  actionLabel,
  note,
  onSlotTaken,
  holdsPass = false,
  preview = false,
}: ProductCardProps) {
  // The grid card is paper in the store, so it reads like the white calls card.
  const onCard = surface === 'card' || !flat;
  const router = useRouter();
  const searchParams = useSearchParams();
  // The store tracks which card *instance* is open, not which bundle — the same
  // bundle can render twice on one page (the trial sits in the calls block and in
  // the products grid), and keying by bundle.id made the second instance toggle
  // the first one closed instead of opening itself.
  const cardId = useId();
  const activeId = useOpenCard((state) => state.activeId);
  const openCard = useOpenCard((state) => state.open);
  const closeCard = useOpenCard((state) => state.close);
  const open = activeId === cardId;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [embedded, setEmbedded] = useState<{
    clientSecret: string;
    publishableKey: string;
  } | null>(null);
  const [checkoutReady, setCheckoutReady] = useState(false);
  const revealCheckout = useCallback(() => setCheckoutReady(true), []);
  const landed = useFreshPass((s) => s.landed);
  const [settling, setSettling] = useState<string | null>(null);
  const [, startRefresh] = useTransition();
  // A signed-out buyer's sign-in carried the alias as `sourceSquad` and
  // anchored the pair on the way in, leaving the hop below nothing to write.
  const anchoredBySignIn = useRef(false);
  // The session behind the form on screen, until it is paid or given up.
  const sessionId = useRef<string | null>(null);
  const unmounted = useRef(false);
  // The form died with the hold it was paying for; the next tap mints both again.
  const staleForm = useRef(false);

  // The owner edits the card itself: it opens with Edit and closes with Save.
  // For everybody else this is inert.
  const patch = useBundlesStore((s) => s.patch);
  const draft = useBundlesStore((s) => s.bundles.find((b) => b.id === bundle.id));
  const editingId = useBundlesStore((s) => s.editingId);
  const edit = useBundlesStore((s) => s.edit);
  const save = useBundlesStore((s) => s.save);
  const cancel = useBundlesStore((s) => s.cancel);
  const catalogueSaving = useBundlesStore((s) => s.saving);
  const saveError = useBundlesStore((s) => s.errors.general ?? null);
  const editorOpen = editable && !!draft && editingId === bundle.id;

  const startCheckout = useCallback(async () => {
    setBusy(true);
    setError(null);
    posthog.capture('checkout_started', {
      provider_username: tutorUsername,
      creator_username: tutorUsername,
      tutor_username: tutorUsername,
      bundle_id: bundle.id,
      bundle_type: bundle.type,
      bundle_price: bundle.price,
      trigger: 'storefront_card',
      is_owner: isOwner,
      slot_picked: !!slot,
    });
    // Anchor the pair before the money is decided: `PaymentsService.order`
    // reads the row this writes. Awaited but never fatal — a lost commission
    // must not cost the provider the sale, and nothing else would ever show
    // that it happened.
    if (squadAlias && !anchoredBySignIn.current) {
      const hop = await callAction(() => recordTeammateHop(squadAlias));
      if (!hop.success && !('transport' in hop)) {
        captureError(new Error(hop.error), {
          module: 'storefront',
          operation: 'teammate_hop_before_checkout',
          extra: { alias: squadAlias, bundleId: bundle.id, tutorUsername },
        });
      }
    }
    const result = await callAction(() =>
      createCheckout({
        tutorUsername,
        bundleId: bundle.id,
        allowPromotionCodes: hasPromoParam(searchParams),
        slot: slot ?? undefined,
      })
    );
    if (!result.success) {
      setBusy(false);
      if (result.error === 'already activated') {
        // Say it in place and re-render — the page then shows their pass.
        // The old router.push of the current URL added a duplicate history
        // entry, so Back appeared to do nothing once.
        openCard(cardId);
        setError('Already yours — your pass is on this page.');
        router.refresh();
        return;
      }
      if (result.error === 'slot taken') {
        onSlotTaken?.();
        return;
      }
      openCard(cardId);
      setError(
        result.error === 'This user is not available'
          ? 'This user is not available'
          : result.error === INTRO_ALREADY_BOUGHT
            ? INTRO_ALREADY_BOUGHT_COPY
            : 'Something went wrong — try again'
      );
      return;
    }
    const { url, clientSecret, publishableKey } = result.data;
    // A returning client's sign-in re-renders the page and takes this card
    // away, leaving the hold it just claimed with nobody to give it up.
    if (unmounted.current) {
      if (slot) void callAction(() => expireCheckout(result.data.sessionId));
      return;
    }
    sessionId.current = result.data.sessionId;
    staleForm.current = false;
    if (clientSecret && publishableKey) {
      setEmbedded({ clientSecret, publishableKey });
      return;
    }
    if (url) {
      // Hosted Stripe is another origin — an honest full document navigation,
      // the same way TipCard leaves. router.push of an external URL fell back
      // to the same full load with an extra router hop in front of it.
      window.location.assign(url);
      return;
    }
    Sentry.captureMessage('Checkout session came back empty', {
      level: 'error',
      extra: { bundleId: bundle.id, tutorUsername, surface: 'storefront_card' },
    });
    setBusy(false);
    openCard(cardId);
    setError('Something went wrong — try again');
  }, [
    bundle.id,
    bundle.type,
    bundle.price,
    tutorUsername,
    isOwner,
    router,
    openCard,
    cardId,
    squadAlias,
    searchParams,
    slot,
    onSlotTaken,
  ]);

  // Closing the card is giving the time up; so is leaving the page. Only a
  // session that holds a slot has anything to give back.
  const giveUpHold = useCallback(() => {
    const id = sessionId.current;
    if (!id || !slot) return;
    sessionId.current = null;
    void callAction(() => expireCheckout(id));
    staleForm.current = true;
  }, [slot]);
  useEffect(() => {
    if (!open) giveUpHold();
  }, [open, giveUpHold]);
  useEffect(() => {
    // Reset on mount: StrictMode runs this cleanup once on a card that stays.
    unmounted.current = false;
    return () => {
      unmounted.current = true;
      giveUpHold();
    };
  }, [giveUpHold]);

  // The session is taken before the close, so the hold — now the webhook's to
  // release — is not expired on the way out.
  const paymentDone = useCallback(() => {
    const id = sessionId.current;
    sessionId.current = null;
    setEmbedded(null);
    setCheckoutReady(false);
    setBusy(false);
    closeCard();
    setSettling(id);
  }, [closeCard]);

  const settled = useCallback(
    (status: PaymentStatusDto) => {
      posthog.capture('order_completed', {
        provider_username: status.tutorUsername,
        creator_username: status.tutorUsername,
        tutor_username: status.tutorUsername,
        bundle_id: status.bundleId,
        bundle_type: status.bundleType,
        ref: status.ref,
        checkout_session_id: status.checkoutSessionId,
        is_paid: true,
        is_free: false,
        // Read after the listener answered, so it cannot lie: booked, lost, or absent.
        slot: status.slot,
      });
      if (holdsPass && status.courseId) landed(tutorUsername, status.courseId);
      // One transition, so the card leaves "Paid" in the same commit that brings the pass.
      startRefresh(() => {
        setSettling(null);
        // A teammate's product bought on the mate page: the pass is on their own storefront.
        if (squadAlias) router.push(`/${tutorUsername}`);
        else router.refresh();
      });
    },
    [holdsPass, landed, tutorUsername, squadAlias, router]
  );

  const handleOpen = useCallback(() => {
    if (open) {
      closeCard();
      return;
    }
    // The tap on the card, before it forks: a signed-out visitor gets the
    // sign-in form (and the auth funnel takes over from here), a signed-in one
    // goes straight to checkout. One step, both paths, so the card's own pull is
    // readable without knowing who tapped it.
    posthog.capture('product_card_opened', {
      provider_username: tutorUsername,
      creator_username: tutorUsername,
      tutor_username: tutorUsername,
      bundle_id: bundle.id,
      bundle_type: bundle.type,
      bundle_price: bundle.price,
      logged_in: loggedIn,
      is_owner: isOwner,
      placement: authSource,
    });
    openCard(cardId);
    if (loggedIn && (!embedded || staleForm.current)) void startCheckout();
  }, [
    open,
    loggedIn,
    embedded,
    startCheckout,
    openCard,
    closeCard,
    cardId,
    bundle.id,
    bundle.type,
    bundle.price,
    tutorUsername,
    isOwner,
    authSource,
  ]);

  const opened = useRef(false);
  useEffect(() => {
    if (!autoOpen || !loggedIn || note || opened.current) return;
    // Once per tab session, not once per mount: a Back navigation restores
    // the storefront with ?offer/?bundle still in the URL and remounts this
    // card, and without the latch the effect relaunched checkout right after
    // the visitor pressed Back — in hosted mode, navigating them straight
    // off the page again. The params themselves must stay in the URL (they
    // are the server render's state: the offer-only view, the focus card), so
    // the latch, not a param strip, is what makes auto-open one-shot. A
    // deliberate tap on the card still works any number of times.
    const latchKey = `mm-auto-checkout:${bundle.id}`;
    try {
      if (sessionStorage.getItem(latchKey)) return;
      sessionStorage.setItem(latchKey, '1');
    } catch {
      // Storage unavailable (private mode quirks): fall back to per-mount.
    }
    opened.current = true;
    const frame = requestAnimationFrame(() => {
      openCard(cardId);
      void startCheckout();
    });
    return () => cancelAnimationFrame(frame);
  }, [autoOpen, loggedIn, note, openCard, startCheckout, cardId, bundle.id]);

  const title =
    bundle.title || getDefaultTitle(bundle.type, bundle.numberOfClasses, bundle.duration);
  // The owner prices their intro here; the fixed TRIAL_TITLE would only name it.
  const ownIntro = editable && bundle.type === BundleType.TRIAL;
  const heading = headline !== undefined ? headline : ownIntro ? OWNER_TRIAL_LINE : title;
  const action =
    actionLabel ??
    (bundle.numberOfClasses > 1
      ? `Get ${bundle.numberOfClasses} calls`
      : `Get ${bundle.duration} min`);
  const total = withFees(bundle.price);
  const previousTotal =
    bundle.previousPrice != null && bundle.previousPrice > bundle.price
      ? withFees(bundle.previousPrice)
      : null;

  // A pack says what one of its calls costs: the number a client compares with the single call.
  const saving =
    bundle.previousPrice != null && bundle.previousPrice > bundle.price
      ? Math.round((1 - bundle.price / bundle.previousPrice) * 100)
      : 0;
  const shape =
    `${bundle.numberOfClasses} ${callNoun(bundle.numberOfClasses)}, ${bundle.duration} min` +
    (bundle.numberOfClasses > 1
      ? ` each, ${formatPrice({ amount: total / bundle.numberOfClasses, currency: bundle.currency })} a call`
      : '') +
    (saving > 0 ? `, save ${saving}%` : '');
  const showAuth = open && !loggedIn;
  const showError = open && !!error;
  const showCheckout = open && checkoutReady;
  const chipVisible = !note && !settling && !(showAuth || showError || showCheckout);
  const chipLoading = busy && !checkoutReady;
  const spinner = (
    <span
      className={tw(
        'h-5 w-5 flex-shrink-0 animate-spin rounded-full border-2',
        onCard
          ? 'border-[color-mix(in_srgb,var(--sf-page)_25%,transparent)] border-t-[var(--sf-page)]'
          : 'border-[#16110f]/20 border-t-[#16110f]'
      )}
    />
  );

  const head = (
    <div className="flex min-w-0 flex-1 flex-col gap-2">
      {heading !== null &&
        // Every intro is titled TRIAL_TITLE, so its title is not the owner's to type.
        (editorOpen && draft && !ownIntro ? (
          <InlineText
            // Remounted when its terms change, which is when a name nobody typed is rewritten.
            key={`${draft.numberOfClasses}-${draft.duration}`}
            defaultValue={draft.title}
            limit={BUNDLE_TITLE_MAX}
            placeholder="Name this product"
            ariaLabel="Product name"
            className="sf-title text-left text-title-2 font-extrabold text-[var(--sf-ink)]"
            onInput={(title) => patch(bundle.id, { title })}
          />
        ) : (
          <span
            className={tw(
              'sf-title text-left text-title-2 font-extrabold',
              onCard ? 'text-[var(--sf-ink)]' : 'text-white'
            )}
          >
            {heading}
          </span>
        ))}

      {/* The owner reads the same facts in the settings below; a visitor reads them here. */}
      {!flat && !editorOpen && (
        <span className="text-p-3 font-semibold text-[var(--sf-mut)]">{shape}</span>
      )}

      {editorOpen && draft && <ProductEditor bundle={draft} />}

      {note && (
        <span
          className={tw(
            'text-p-3 font-semibold',
            onCard ? 'text-[var(--sf-fnt)]' : 'text-white/80'
          )}
        >
          {note}
        </span>
      )}

      {chipVisible && (
        <button
          type="button"
          aria-expanded={open || checkoutReady}
          onClick={preview ? undefined : handleOpen}
          className={tw('mm-btn sf-cta sf-cta-priced mt-1', onCard && 'sf-cta-ink')}
        >
          {chipLoading ? (
            <span className="mx-auto flex">{spinner}</span>
          ) : (
            <>
              <span>{action}</span>
              <span className="ml-auto flex items-baseline gap-1.5">
                {previousTotal != null && (
                  <span
                    className={tw(
                      'sf-num text-p-3 font-semibold line-through',
                      onCard
                        ? 'text-[color-mix(in_srgb,var(--sf-page)_55%,transparent)]'
                        : 'text-[#16110f]/40'
                    )}
                  >
                    {formatPrice({ amount: previousTotal, currency: bundle.currency })}
                  </span>
                )}
                <span className="sf-num text-head-2 font-extrabold">
                  {formatPrice({ amount: total, currency: bundle.currency })}
                </span>
              </span>
            </>
          )}
        </button>
      )}

      {settling && (
        <PaidWait
          sessionId={settling}
          onSettled={settled}
          className={tw('mm-btn sf-cta mt-1 gap-2', onCard && 'sf-cta-ink')}
          spinner={spinner}
        />
      )}

      {editorOpen && (
        <p className="text-p-3 text-[var(--sf-mut)]">
          {priceSplitNote(bundle.price, bundle.currency)}
        </p>
      )}

      {editable &&
        (editorOpen ? (
          <SaveCancel
            onSave={() => void save()}
            onCancel={cancel}
            saving={catalogueSaving}
            error={saveError}
          />
        ) : (
          <EditButton
            onClick={() => edit(bundle.id)}
            disabled={editingId !== null || catalogueSaving}
          />
        ))}
    </div>
  );

  return (
    <div
      className={tw(
        'flex flex-col gap-3 overflow-hidden',
        !flat && 'sf-prod-card rounded-[var(--sf-radius)] p-3.5'
      )}
    >
      {head}

      {(showAuth || showError) && (
        <div>
          {showError ? (
            <div className="flex flex-col gap-2">
              <span className={tw('text-p-3 font-semibold', onCard ? 'text-error' : 'text-white')}>
                {error}
              </span>
              <button
                type="button"
                onClick={() => void startCheckout()}
                className={tw('mm-btn sf-cta', onCard && 'sf-cta-ink')}
              >
                Try again
              </button>
            </div>
          ) : (
            <InlineAuth
              id={`product-${bundle.id}`}
              source={authSource}
              tutorUsername={tutorUsername}
              sourceSquad={squadAlias}
              tone={onCard ? 'card' : 'accent'}
              prompt={authPrompt ?? `Sign in to get ${title}`}
              cta="Continue to checkout"
              onSuccess={() => {
                // The cookie write re-rendered the page first: a returning
                // client's card is already gone, and this sale with it; the
                // intro card is left to say why.
                if (unmounted.current) {
                  useCutSale.getState().cut(tutorUsername);
                  return;
                }
                anchoredBySignIn.current = true;
                return startCheckout();
              }}
            />
          )}
        </div>
      )}

      {open && embedded && (
        <div
          className={tw(
            'grid transition-[grid-template-rows] duration-300 ease-out',
            checkoutReady ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
          )}
        >
          <div className="sf-clip">
            <EmbeddedCheckoutPanel
              clientSecret={embedded.clientSecret}
              publishableKey={embedded.publishableKey}
              onPainted={revealCheckout}
              onComplete={paymentDone}
            />
          </div>
        </div>
      )}
    </div>
  );
}

/** The money is in; the pass is the webhook's to make. Mounted only while it does. */
function PaidWait({
  sessionId,
  onSettled,
  className,
  spinner,
}: {
  sessionId: string;
  onSettled: (status: PaymentStatusDto) => void;
  className: string;
  spinner: ReactNode;
}) {
  const { status, bookingCall } = usePaymentPolling(sessionId);
  useEffect(() => {
    if (status) onSettled(status);
  }, [status, onSettled]);
  return (
    <div role="status" className={className}>
      {spinner}
      <span>{bookingCall ? 'Paid, booking your call' : 'Paid'}</span>
    </div>
  );
}
